import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  ArrowUpFromLine, ArrowDownToLine, ChevronDown, ChevronRight,
  Paperclip, FileText, Download, CheckCircle, Trash2
} from "lucide-react";
import {
  TYPE_LABELS, TYPE_COLORS, STATUS_COLORS, secureDownloadFile
} from "./commHelpers";
import RelanceButton from "@/components/shared/RelanceButton";
import ReadToggleButton from "@/components/shared/ReadToggleButton";
import ArchiveButton from "@/components/admin/ArchiveButton";

export default function CommList({ items, direction, onRefresh, isOverridden, markRead, markUnread }) {
  const [expandedId, setExpandedId] = useState(null);

  const typeLabel = (t) => TYPE_LABELS[t] || t;
  const typeColor = (t) => TYPE_COLORS[t] || "bg-gray-100 text-gray-600";
  const statusColor = (s) => STATUS_COLORS[s] || "bg-gray-100 text-gray-600";

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette communication?")) return;
    try {
      await api.delete(`/communications/${id}`);
      toast.success("Supprime");
      onRefresh();
    } catch { toast.error("Erreur"); }
  };

  const handleMarkComplete = async (id) => {
    try {
      await api.put(`/communications/${id}`, { status: "complete" });
      toast.success("Marque comme complete");
      onRefresh();
    } catch { toast.error("Erreur"); }
  };

  if (!items.length) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="empty-list">
        <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
          {direction === "sent" ? <ArrowUpFromLine className="w-5 h-5" /> : <ArrowDownToLine className="w-5 h-5" />}
        </div>
        <p className="text-sm">Aucune communication</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="comm-list">
      {items.map(comm => {
        const expanded = expandedId === comm.id;
        const client = comm._client_name || "";
        return (
          <div key={comm.id} className="border rounded-xl overflow-hidden bg-white hover:shadow-sm transition">
            <button onClick={() => setExpandedId(expanded ? null : comm.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              data-testid={`comm-${comm.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${typeColor(comm.type)}`}>
                    {typeLabel(comm.type)}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColor(comm.status)}`}>
                    {comm.status}
                  </span>
                  {comm.tax_year && (
                    <span className="text-[10px] text-gray-400">{comm.tax_year}</span>
                  )}
                  {comm.attachments?.length > 0 && (
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <Paperclip className="w-3 h-3" /> {comm.attachments.length}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-900 mt-1 truncate">{comm.title}</p>
                {client && <p className="text-xs text-gray-500">{client}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-gray-400">{new Date(comm.created_at).toLocaleDateString("fr-CA")}</p>
                <p className="text-[10px] text-gray-300">
                  {new Date(comm.created_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            </button>

            {/* Bouton "Marquer comme lu" — Batch #14.93 */}
            {markRead && isOverridden && (
              <div className="px-3 pb-2 -mt-2 flex items-center gap-2 text-[10px] text-slate-500">
                <ReadToggleButton
                  isReallyUnread={!comm.read_at}
                  isOverridden={isOverridden(comm.id)}
                  onMarkRead={() => markRead(comm.id)}
                  onMarkUnread={() => markUnread(comm.id)}
                  testId={`comm-toggle-${comm.id}`}
                />
                {isOverridden(comm.id) && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Marqué lu (admin)
                  </span>
                )}
              </div>
            )}

            {expanded && (
              <div className="px-4 pb-4 border-t bg-gray-50/50">
                {comm.message && <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{comm.message}</p>}
                {comm.attachments?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium text-gray-500">Pieces jointes :</p>
                    {comm.attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border text-sm">
                        <span className="flex items-center gap-2 truncate">
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          {att.filename}
                          {att.size && (
                            <span className="text-gray-400 text-xs">
                              ({(att.size / 1024).toFixed(0)} KB)
                            </span>
                          )}
                        </span>
                        <button onClick={() => secureDownloadFile(att.url, att.filename)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid={`download-${att.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {comm.status !== "complete" && direction === "received" && (
                    <button onClick={() => handleMarkComplete(comm.id)}
                      className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium">
                      <CheckCircle className="w-3.5 h-3.5" /> Marquer complete
                    </button>
                  )}
                  {/* Relance — only on sent/admin → client items, when status is still pending */}
                  {direction === "sent" && comm.status !== "complete" && comm.client_id && (
                    <RelanceButton
                      clientId={comm.client_id}
                      targetType={comm.comm_type === "demande_document" ? "demande" : comm.comm_type === "document" ? "document" : "message"}
                      targetId={comm.id}
                      variant="small"
                    />
                  )}
                  <button onClick={() => handleDelete(comm.id)}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                  <ArchiveButton
                    itemType="communication"
                    itemId={comm.id}
                    itemLabel="cette communication"
                    variant="button"
                    onArchived={onRefresh}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
